import type { Tables } from '@aikoa/common'
import { z } from 'zod/v4'
import { latLngSchema, type LatLng } from '../geography/types.ts'

export const addressSchema = z.object({
  street: z.string().min(1),
  street_number: z.string(),
  postal_code: z.string(),
  city: z.string(),
  municipality: z.string().nullish(),
  region: z.string().nullish(),
  state: z.string().nullish(),
  neighborhood: z.string().nullish(),
  country: z.string().default('Finland'),
})

// Infer the type from the schema
export type Address = z.infer<typeof addressSchema>

export const assetAddressSchema = addressSchema.extend({
  unit_number: z.string().nullish(),
})

export type AssetAddress = z.infer<typeof assetAddressSchema>

export const addressConnectionDataSchema = z.object({
  unitNumber: z.string().nullish(),
})

export const addressConnectionSchema = z.object({
  id: z.string(),
  address_id: z.string(),
  asset_id: z.string(),
  relation_type: z.enum(['primary']).nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  data: addressConnectionDataSchema.optional(),
})

export type AddressConnection = z.infer<typeof addressConnectionSchema>

export const parsedAddressSchema = z.object({
  id: z.string(),
  address: addressSchema,
  address_type: z.string().default('primary'),
  data: z.unknown(), // TODO: add data schema when data is added
  created_at: z.string(),
  updated_at: z.string(),
  address_connections: z.array(addressConnectionSchema).default([]),
})

export type ParsedAddress = z.infer<typeof parsedAddressSchema>

export type AddressAutocompleteResult = {
  id: string
  geom: LatLng | null
  address: Address
}

type RawAddress = Omit<Tables<'addresses'>, 'address_hash'>
type RawAddressWithConnection = RawAddress & {
  address_connections?: Tables<'address_connections'>[]
}

export function createParsedAddress(address: RawAddressWithConnection): ParsedAddress {
  return parsedAddressSchema.parse({
    ...address,
    address_connections: address.address_connections ?? [],
  })
}

export function createParsedAddresses(addresses: RawAddressWithConnection[]): ParsedAddress[] {
  return addresses.map((addr) => createParsedAddress(addr))
}

export type ConnectAddress =
  | {
      address: Address
      location: LatLng
    }
  | {
      addressId: string
    }

export function isParsedAddress(address: unknown): address is ParsedAddress {
  return parsedAddressSchema.safeParse(address).success
}

export function isAddressAutocompleteResult(
  address: unknown
): address is AddressAutocompleteResult {
  return (
    typeof address === 'object' &&
    address !== null &&
    'id' in address &&
    !!address.id &&
    'geom' in address &&
    latLngSchema.safeParse(address.geom).success &&
    'address' in address &&
    addressSchema.safeParse(address.address).success
  )
}
